import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/** Sinaliza configuração ausente para a UI mostrar um aviso claro. */
export const isSupabaseConfigured = Boolean(url && anonKey)

if (!isSupabaseConfigured) {
  const aviso =
    'VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY não definidas. ' +
    'Em desenvolvimento, copie .env.example para .env.local. ' +
    'Em produção, defina as duas variáveis no painel de hospedagem e refaça o build.'

  // Em produção, cair no endereço local em silêncio produziria um app que
  // "carrega" e falha em toda requisição, sem dizer o motivo. Melhor quebrar
  // no primeiro segundo, com a causa escrita.
  if (import.meta.env.PROD) throw new Error(`[SocialHub] ${aviso}`)
  console.warn(`[SocialHub] ${aviso}`)
}

export const supabase: SupabaseClient = createClient(
  url ?? 'http://127.0.0.1:54321',
  anonKey ?? 'anon-key-nao-configurada',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'socialhub.auth',
    },
  },
)

export interface EdgeFunctionError {
  code: string
  message: string
  detail?: unknown
}

/**
 * Chama uma Edge Function do BFF já traduzindo o erro para uma mensagem
 * exibível. A Edge Function sempre responde `{ error: { code, message } }`.
 */
export async function invokeFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T | { error: EdgeFunctionError }>(name, {
    body,
  })

  if (error) {
    // `FunctionsHttpError` carrega o corpo da resposta; extraímos a mensagem real.
    const contextResponse = (error as { context?: Response }).context
    if (contextResponse && typeof contextResponse.json === 'function') {
      try {
        const payload = (await contextResponse.json()) as { error?: EdgeFunctionError }
        if (payload?.error?.message) {
          throw new FunctionError(payload.error.code, payload.error.message, payload.error.detail)
        }
      } catch (parseError) {
        if (parseError instanceof FunctionError) throw parseError
      }
    }
    throw new FunctionError('network_error', error.message || 'Falha de comunicação com o servidor.')
  }

  if (data && typeof data === 'object' && 'error' in data) {
    const payload = (data as { error: EdgeFunctionError }).error
    throw new FunctionError(payload.code, payload.message, payload.detail)
  }

  return data as T
}

export class FunctionError extends Error {
  readonly code: string
  readonly detail?: unknown

  constructor(code: string, message: string, detail?: unknown) {
    super(message)
    this.name = 'FunctionError'
    this.code = code
    this.detail = detail
  }
}
